import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CMIcon, DomainGlyph, RingGauge, MiniBar, Mono } from '../../ui/primitives';
import { SectionHead } from '../../ui/SectionHead';
import {
  BAR_FILL_CLASS,
  TIER_CHIP_CLASS,
  DIFF_CHIP_CLASS,
  getIqTier,
} from './palette';
import type {
  DomainVM,
  DrillVM,
  GlobalIQ,
  NextMilestoneVM,
  ScoreTrendPointVM,
} from './types';

export { SectionHead };

/* ──────────────────────────────────────────────────────────────────────
 * Greeting — overline + headline + sub + two CTAs.
 * ──────────────────────────────────────────────────────────────────── */
interface GreetingProps {
  userName?: string | null;
  certName?: string | null;
  daysAhead?: number | null;
  daysToExam?: number | null;
}

export function Greeting({ userName, certName, daysAhead, daysToExam }: GreetingProps) {
  const { t, i18n } = useTranslation();
  const now = new Date();
  const hour = now.getHours();
  const greetKey =
    hour < 12
      ? 'dashboard.variantB.greeting.morning'
      : hour < 18
      ? 'dashboard.variantB.greeting.afternoon'
      : 'dashboard.variantB.greeting.evening';
  const locale = i18n.language === 'fr' ? 'fr-FR' : 'en-US';
  const dateLabel = now.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0">
        <p className="text-[11px] font-extrabold uppercase tracking-[0.13em] text-blue-600 dark:text-blue-400">
          {t(greetKey)} · {dateLabel}
        </p>
        <h1 className="mt-2 text-[28px] font-extrabold tracking-[-0.03em] text-slate-900 dark:text-white sm:text-[32px]">
          {userName
            ? t('dashboard.variantB.greeting.titleWithName', { name: userName })
            : t('dashboard.variantB.greeting.titleFallback')}
          <span className="text-slate-500 dark:text-slate-400">
            {' '}· {t('dashboard.variantB.greeting.tagline')}
          </span>
        </h1>
        <p className="mt-2 max-w-2xl text-[13.5px] text-slate-600 dark:text-slate-300">
          {certName && daysToExam != null && daysAhead != null
            ? t('dashboard.variantB.greeting.subFull', {
                cert: certName,
                ahead: daysAhead,
                days: daysToExam,
              })
            : certName
            ? t('dashboard.variantB.greeting.subCertOnly', { cert: certName })
            : t('dashboard.variantB.greeting.subFallback')}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Link
          to="/study"
          className="inline-flex items-center gap-2 rounded-[11px] bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-[0_6px_16px_-6px_rgba(37,99,235,0.6)] transition-all hover:bg-blue-700 hover:shadow-[0_8px_20px_-6px_rgba(37,99,235,0.7)]"
        >
          <CMIcon name="play" size={15} fill />
          {t('dashboard.variantB.greeting.resumeDrill')}
        </Link>
        <Link
          to="/exam"
          className="inline-flex items-center gap-2 rounded-[11px] border border-slate-200 bg-white/80 px-4 py-2.5 text-sm font-semibold text-slate-700 backdrop-blur transition-colors hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10"
        >
          <CMIcon name="filecheck" size={15} />
          {t('dashboard.variantB.greeting.mockExam')}
        </Link>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Recommended hero — full-width card with pulse pill + glyph + CTAs.
 * ──────────────────────────────────────────────────────────────────── */
interface RecommendedProps {
  drill: DrillVM;
  domain: DomainVM | null;
  whyKey: string;
  examWeight: number | null;
}

export function RecommendedHero({ drill, domain, whyKey, examWeight }: RecommendedProps) {
  const { t } = useTranslation();
  return (
    <div className="card-shell relative mb-6 overflow-hidden rounded-2xl border border-slate-200/70 bg-white p-5 shadow-[0_2px_4px_rgba(15,23,42,0.05),0_6px_16px_-6px_rgba(15,23,42,0.12)] dark:border-white/10 dark:bg-card-dark sm:p-6">
      <span
        className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-[11px] font-extrabold uppercase tracking-[0.13em] text-blue-700 dark:bg-blue-500/10 dark:text-blue-300"
      >
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full rounded-full bg-blue-500 opacity-60 motion-safe:animate-ping" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-500" />
        </span>
        {t('dashboard.variantB.recommended.tag')}
      </span>

      <div className="mt-4 flex flex-wrap items-start gap-5">
        {domain && (
          <DomainGlyph icon={domain.icon} gradient={domain.gradient} size={64} radius={16} iconScale={0.5} />
        )}
        <div className="min-w-0 flex-1">
          <h3 className="text-[22px] font-extrabold tracking-[-0.03em] text-slate-900 dark:text-white">
            {t('dashboard.variantB.recommended.reinforce')}{' '}
            <span className="text-blue-600 dark:text-blue-400">{drill.domName}</span>
          </h3>
          <p className="mt-2 max-w-2xl text-[13.5px] text-slate-600 dark:text-slate-300">
            {t(whyKey, { mins: drill.mins })}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Chip className={DIFF_CHIP_CLASS[drill.diff]}>
              {t(`dashboard.variantB.difficulty.${drill.diff}`)}
            </Chip>
            <Chip>
              <CMIcon name="layers" size={13} />
              {t('dashboard.variantB.recommended.questionsCount', { count: drill.q })}
            </Chip>
            <Chip>
              <CMIcon name="clock" size={13} />
              {t('dashboard.variantB.recommended.minutes', { count: drill.mins })}
            </Chip>
            {examWeight != null && examWeight > 0 && (
              <Chip>
                <CMIcon name="target" size={13} />
                {t('dashboard.variantB.recommended.examWeight', { percent: examWeight })}
              </Chip>
            )}
          </div>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        <Link
          to={drill.href}
          className="inline-flex items-center gap-2 rounded-[11px] bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-[0_6px_16px_-6px_rgba(37,99,235,0.6)] transition-all hover:bg-blue-700"
        >
          <CMIcon name="zap" size={15} fill />
          {t('dashboard.variantB.recommended.startSession')}
        </Link>
        <Link
          to="/study"
          className="inline-flex items-center gap-2 rounded-[11px] border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 dark:border-white/10 dark:text-slate-200 dark:hover:bg-white/10"
        >
          {t('dashboard.variantB.recommended.pickAnother')}
        </Link>
      </div>
    </div>
  );
}

function Chip({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-[11.5px] font-semibold text-slate-700 dark:bg-white/5 dark:text-slate-300 ${className}`}
    >
      {children}
    </span>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Skill IQ gauge card.
 * ──────────────────────────────────────────────────────────────────── */
interface SkillIQCardProps {
  iq: GlobalIQ;
}

export function SkillIQCard({ iq }: SkillIQCardProps) {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-[0_2px_4px_rgba(15,23,42,0.05),0_6px_16px_-6px_rgba(15,23,42,0.12)] dark:border-white/10 dark:bg-card-dark">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[13px] font-extrabold text-slate-900 dark:text-white">
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300">
            <CMIcon name="brain" size={13} />
          </span>
          {t('dashboard.variantB.skillIQ.title')}
        </div>
        <span className={`rounded-full px-2.5 py-1 text-[11px] font-extrabold uppercase tracking-wider ${TIER_CHIP_CLASS[iq.tier.key]}`}>
          {t(iq.tier.labelKey)}
        </span>
      </div>

      <div className="relative mx-auto mt-1 flex h-[196px] w-[196px] items-center justify-center">
        <RingGauge value={iq.score} target={iq.target} size={196} stroke={16} className="absolute inset-0" />
        <div className="relative flex flex-col items-center text-center">
          <Mono className="text-[44px] font-extrabold leading-none text-slate-900 dark:text-white">
            {iq.score}
          </Mono>
          <p className="mt-1 text-[11.5px] font-semibold text-slate-500 dark:text-slate-400">
            {t('dashboard.variantB.skillIQ.outOf', { target: iq.target })}
          </p>
          {iq.delta !== 0 && (
            <p className="mt-1.5 inline-flex items-center gap-1 text-[11.5px] font-extrabold text-green-600 dark:text-green-400">
              <CMIcon name="arrowup" size={11} />
              {iq.delta > 0 ? '+' : ''}{iq.delta} {t('dashboard.variantB.skillIQ.pts30d')}
            </p>
          )}
        </div>
      </div>

      {iq.attempts > 0 && (
        <div className="mt-3 grid grid-cols-3 divide-x divide-slate-200 border-t border-slate-200 pt-3 dark:divide-white/10 dark:border-white/10">
          <Stat value={iq.percentile != null ? `${iq.percentile}` : '—'} label={t('dashboard.variantB.skillIQ.percentile')} />
          <Stat value={iq.peers != null ? String(iq.peers) : '—'} label={t('dashboard.variantB.skillIQ.cohortAvg')} />
          <Stat value={String(iq.attempts)} label={t('dashboard.variantB.skillIQ.assessments')} />
        </div>
      )}
    </div>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div className="px-2 text-center">
      <Mono className="block text-[18px] font-extrabold text-slate-900 dark:text-white">{value}</Mono>
      <span className="text-[10.5px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
        {label}
      </span>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Next milestone mini card.
 * ──────────────────────────────────────────────────────────────────── */
export function NextMilestoneCard({ milestone }: { milestone: NextMilestoneVM }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl border border-slate-200/70 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-card-dark">
      <p className="text-[10.5px] font-extrabold uppercase tracking-[0.13em] text-slate-500 dark:text-slate-400">
        {t('dashboard.variantB.milestone.title')}
      </p>
      <div className="mt-3 flex items-center gap-3">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300">
          <CMIcon name="trophy" size={19} />
        </span>
        <div className="min-w-0 leading-tight">
          <p className="text-[13.5px] font-extrabold text-slate-900 dark:text-white">
            {t('dashboard.variantB.milestone.reach', { score: milestone.targetScore })}
          </p>
          <p className="text-[11.5px] font-semibold text-slate-500 dark:text-slate-400">
            {t('dashboard.variantB.milestone.toGo', { points: milestone.pointsToGo, sessions: milestone.sessionsEstimate })}
          </p>
        </div>
      </div>
      <MiniBar value={milestone.progressPct} fillClass="bg-blue-600 dark:bg-blue-500" className="mt-4" />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Learning path — vertical timeline of domain steps.
 * ──────────────────────────────────────────────────────────────────── */
export function LearningPath({ steps }: { steps: DomainVM[] }) {
  const { t } = useTranslation();
  if (steps.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500 dark:border-white/10 dark:text-slate-400">
        {t('dashboard.variantB.path.empty')}
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-[0_2px_4px_rgba(15,23,42,0.05),0_6px_16px_-6px_rgba(15,23,42,0.12)] dark:border-white/10 dark:bg-card-dark">
      <ul className="space-y-0">
        {steps.map((d, i) => {
          const last = i === steps.length - 1;
          return (
            <li key={d.code} className="flex gap-4">
              {/* Rail */}
              <div className="flex flex-col items-center">
                <PathNode state={d.state ?? 'todo'} code={d.code} />
                {!last && (
                  <div
                    className={`mt-1 w-0.5 flex-1 ${
                      d.state === 'done'
                        ? 'bg-green-400/70 dark:bg-green-500/50'
                        : 'bg-slate-200 dark:bg-white/10'
                    }`}
                  />
                )}
              </div>
              {/* Card */}
              <div className="flex-1 pb-4">
                <div className="rounded-xl border border-slate-200/70 bg-slate-50/60 p-3 dark:border-white/10 dark:bg-white/5">
                  <p className="text-[13.5px] font-extrabold text-slate-900 dark:text-white">
                    {d.name}
                  </p>
                  <div className="mt-2 flex items-center gap-3">
                    <DomainGlyph icon={d.icon} gradient={d.gradient} size={38} radius={11} iconScale={0.5} />
                    <div className="min-w-0 flex-1 text-[12px] text-slate-600 dark:text-slate-300">
                      {d.state === 'done' && (
                        <>
                          {t('dashboard.variantB.path.completed')} ·{' '}
                          <b className="text-slate-900 dark:text-white">
                            <Mono>{Math.round(d.progress * 100)}%</Mono>
                          </b>{' '}
                          {t('dashboard.variantB.path.mastery')}
                        </>
                      )}
                      {d.state === 'cur' && (
                        <>
                          {t('dashboard.variantB.path.inProgress')} ·{' '}
                          <b className="text-slate-900 dark:text-white">
                            <Mono>{d.done}/{d.lessons}</Mono>
                          </b>{' '}
                          {t('dashboard.variantB.path.lessons')} ·{' '}
                          {t('dashboard.variantB.path.minutesLeft', { mins: d.mins })}
                        </>
                      )}
                      {d.state === 'todo' && (
                        <>
                          {t('dashboard.variantB.path.upNext')} ·{' '}
                          <b className="text-slate-900 dark:text-white">
                            <Mono>{d.lessons}</Mono>
                          </b>{' '}
                          {t('dashboard.variantB.path.lessons')} ·{' '}
                          {t('dashboard.variantB.path.examWeight', { percent: d.weight })}
                        </>
                      )}
                    </div>
                    {d.state === 'cur' ? (
                      <Link
                        to={`/study?topic=${encodeURIComponent(d.name)}`}
                        className="inline-flex items-center gap-1.5 rounded-[9px] bg-blue-600 px-3 py-1.5 text-[12.5px] font-semibold text-white transition-colors hover:bg-blue-700"
                      >
                        <CMIcon name="play" size={12} fill />
                        {t('dashboard.variantB.path.continue')}
                      </Link>
                    ) : d.state === 'todo' ? (
                      <span className="text-slate-400 dark:text-slate-500" aria-hidden="true">
                        <CMIcon name="lock" size={14} />
                      </span>
                    ) : (
                      <span className="text-green-500 dark:text-green-400" aria-hidden="true">
                        <CMIcon name="check" size={14} />
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function PathNode({ state, code }: { state: 'done' | 'cur' | 'todo'; code: string }) {
  if (state === 'done') {
    return (
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-green-500 text-white shadow-sm">
        <CMIcon name="check" size={16} />
      </span>
    );
  }
  if (state === 'cur') {
    return (
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-600 text-white shadow-sm ring-4 ring-blue-400/30 dark:ring-blue-500/40">
        <Mono className="text-[11px] font-extrabold">{code}</Mono>
      </span>
    );
  }
  return (
    <span className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-slate-300 bg-white text-slate-500 dark:border-white/20 dark:bg-slate-800 dark:text-slate-400">
      <Mono className="text-[11px] font-extrabold">{code}</Mono>
    </span>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Drills carousel — horizontal scroll-snap row.
 * ──────────────────────────────────────────────────────────────────── */
export function DrillsCarousel({ drills, whyTemplateKey }: { drills: DrillVM[]; whyTemplateKey: string }) {
  const { t } = useTranslation();
  if (drills.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500 dark:border-white/10 dark:text-slate-400">
        {t('dashboard.variantB.drills.empty')}
      </div>
    );
  }
  return (
    <div className="-mx-2 overflow-x-auto px-2 pb-3 [scrollbar-width:thin]">
      <ul className="flex snap-x snap-mandatory gap-4">
        {drills.map((drill) => (
          <li
            key={drill.id}
            className="w-[268px] flex-shrink-0 snap-start"
          >
            <DrillCard drill={drill} whyTemplateKey={whyTemplateKey} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function DrillCard({ drill, whyTemplateKey }: { drill: DrillVM; whyTemplateKey: string }) {
  const { t } = useTranslation();
  return (
    <Link
      to={drill.href}
      className="group flex h-full flex-col rounded-2xl border border-slate-200/70 bg-white p-4 shadow-sm transition-all duration-200 hover:-translate-y-[3px] hover:border-slate-300 hover:shadow-[0_4px_8px_rgba(15,23,42,0.06),0_18px_40px_-12px_rgba(15,23,42,0.2)] dark:border-white/10 dark:bg-card-dark dark:hover:border-white/20"
    >
      <div className="flex items-center justify-between">
        <span className={`rounded-full px-2.5 py-0.5 text-[10.5px] font-extrabold uppercase tracking-wider ${DIFF_CHIP_CLASS[drill.diff]}`}>
          {t(`dashboard.variantB.difficulty.${drill.diff}`)}
        </span>
        <span className="flex items-center gap-1.5 text-[11.5px] font-semibold text-slate-500 dark:text-slate-400">
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: drill.domDot }} aria-hidden="true" />
          <span className="truncate">{drill.domName}</span>
        </span>
      </div>
      <p className="mt-3 text-[15px] font-extrabold leading-tight text-slate-900 dark:text-white line-clamp-2">
        {drill.name}
      </p>
      <p className="mt-2 line-clamp-2 text-[12.5px] text-slate-600 dark:text-slate-300">
        {t(whyTemplateKey, { topic: drill.domName })}
      </p>
      <div className="mt-3 flex items-center gap-2 text-[11.5px] font-semibold text-slate-500 dark:text-slate-400">
        <CMIcon name="layers" size={13} />
        <Mono>{drill.q}</Mono> {t('dashboard.variantB.drills.qShort')}
        <span aria-hidden="true">·</span>
        <CMIcon name="clock" size={13} />
        <Mono>{drill.mins}</Mono> {t('dashboard.variantB.drills.minShort')}
      </div>
      <div className="mt-auto pt-4">
        <span className="flex w-full items-center justify-center gap-1.5 rounded-[11px] border border-slate-200 px-3 py-2 text-[13px] font-semibold text-slate-700 transition-colors group-hover:border-blue-600 group-hover:bg-blue-600 group-hover:text-white dark:border-white/10 dark:text-slate-200 dark:group-hover:bg-blue-600 dark:group-hover:text-white">
          <CMIcon name="play" size={13} fill />
          {t('dashboard.variantB.drills.startDrill')}
        </span>
      </div>
    </Link>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Domain catalog — 3-column grid of "course" cards with per-domain IQ.
 * ──────────────────────────────────────────────────────────────────── */
export function DomainCatalog({ domains, passMark }: { domains: DomainVM[]; passMark: number }) {
  const { t } = useTranslation();
  if (domains.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500 dark:border-white/10 dark:text-slate-400">
        {t('dashboard.variantB.catalog.empty')}
      </div>
    );
  }
  return (
    <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {domains.map((d) => (
        <li key={d.code}>
          <DomainCard d={d} passMark={passMark} />
        </li>
      ))}
    </ul>
  );
}

function DomainCard({ d, passMark }: { d: DomainVM; passMark: number }) {
  const { t } = useTranslation();
  const tier = getIqTier(d.mastery, passMark);
  const pct = Math.round(d.progress * 100);
  const tickAt = Math.max(0, Math.min(100, passMark));
  const statusKey = tier.pass
    ? d.mastery >= 80
      ? 'dashboard.variantB.catalog.status.onTrack'
      : 'dashboard.variantB.catalog.status.building'
    : 'dashboard.variantB.catalog.status.needsFocus';
  const statusIcon = tier.pass ? (d.mastery >= 80 ? 'check' : 'activity') : 'target';
  const statusClass = tier.pass
    ? d.mastery >= 80
      ? 'text-green-600 dark:text-green-400'
      : 'text-blue-600 dark:text-blue-400'
    : 'text-red-600 dark:text-red-400';

  return (
    <Link
      to={`/study?topic=${encodeURIComponent(d.name)}`}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-sm transition-all duration-200 hover:-translate-y-[3px] hover:border-slate-300 hover:shadow-[0_4px_8px_rgba(15,23,42,0.06),0_18px_40px_-12px_rgba(15,23,42,0.2)] dark:border-white/10 dark:bg-card-dark dark:hover:border-white/20"
    >
      {/* Cover */}
      <div
        className="relative h-24 w-full"
        style={{ background: `linear-gradient(135deg, ${d.gradient[0]}, ${d.gradient[1]})` }}
      >
        <svg className="absolute inset-0 h-full w-full" viewBox="0 0 200 96" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
          <circle cx="170" cy="20" r="44" fill="rgba(255,255,255,0.10)" />
          <circle cx="200" cy="70" r="30" fill="rgba(255,255,255,0.07)" />
        </svg>
        <div className="absolute bottom-3 left-3">
          <DomainGlyph icon={d.icon} gradient={d.gradient} size={44} radius={12} iconScale={0.5} className="shadow-md" />
        </div>
        <span className="absolute right-3 top-3 rounded-full bg-black/25 px-2 py-0.5 text-[10.5px] font-extrabold text-white backdrop-blur">
          <Mono>{d.code}</Mono>
        </span>
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col p-4">
        <p className="line-clamp-2 text-[14.5px] font-extrabold text-slate-900 dark:text-white">
          {d.name}
        </p>
        <p className="mt-1 flex flex-wrap items-center gap-1.5 text-[11.5px] font-semibold text-slate-500 dark:text-slate-400">
          <span>{t(d.levelKey)}</span>
          <span aria-hidden="true">·</span>
          <span>
            <Mono>{d.lessons}</Mono> {t('dashboard.variantB.catalog.lessons')}
          </span>
          <span aria-hidden="true">·</span>
          <span>
            <Mono>{d.weight}%</Mono> {t('dashboard.variantB.catalog.ofExam')}
          </span>
        </p>

        {/* IQ row */}
        <div className="mt-3 flex items-end justify-between border-t border-slate-100 pt-3 dark:border-white/10">
          <div>
            <p className="text-[10px] font-extrabold uppercase tracking-[0.13em] text-slate-500 dark:text-slate-400">
              {t('dashboard.variantB.catalog.skillIQ')}
            </p>
            <p className="mt-0.5 flex items-baseline gap-1.5">
              <Mono className="text-[22px] font-extrabold text-slate-900 dark:text-white">{d.mastery}</Mono>
              <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">/100</span>
              {d.iqDelta > 0 && (
                <span className="inline-flex items-center gap-0.5 text-[11px] font-extrabold text-green-600 dark:text-green-400">
                  <CMIcon name="arrowup" size={10} /> {d.iqDelta}
                </span>
              )}
            </p>
          </div>
          <span className={`rounded-full px-2.5 py-1 text-[10.5px] font-extrabold uppercase tracking-wider ${TIER_CHIP_CLASS[tier.key]}`}>
            {t(tier.labelKey)}
          </span>
        </div>

        {/* Metrics */}
        <div className="mt-3 space-y-2">
          <Metric
            label={t('dashboard.variantB.catalog.masteryLabel')}
            value={<><Mono>{d.mastery}</Mono></>}
            bar={<MiniBar value={d.mastery} fillClass={BAR_FILL_CLASS[tier.barColor]} tickAt={tickAt} />}
          />
          <Metric
            label={t('dashboard.variantB.catalog.completionLabel')}
            value={<><Mono>{pct}</Mono>%</>}
            bar={<MiniBar value={pct} fillClass="bg-blue-600 dark:bg-blue-500" />}
          />
        </div>

        {/* Foot */}
        <div className="mt-auto flex items-center justify-between border-t border-slate-100 pt-3 dark:border-white/10">
          <span className={`inline-flex items-center gap-1.5 text-[11.5px] font-extrabold ${statusClass}`}>
            <CMIcon name={statusIcon} size={13} />
            {t(statusKey)}
          </span>
          <span className="inline-flex items-center gap-0.5 text-[11.5px] font-semibold text-slate-500 group-hover:text-blue-600 dark:text-slate-400 dark:group-hover:text-blue-400">
            {t('dashboard.variantB.catalog.continue')}
            <CMIcon name="chevright" size={13} />
          </span>
        </div>
      </div>
    </Link>
  );
}

function Metric({ label, value, bar }: { label: string; value: React.ReactNode; bar: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-[11.5px] font-semibold text-slate-500 dark:text-slate-400">
        <span>{label}</span>
        <b className="text-slate-900 dark:text-white">{value}</b>
      </div>
      {bar}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Score trajectory — full-width SVG line chart with PASS line.
 * Mirrors the design's CMScoreTrend.
 * ──────────────────────────────────────────────────────────────────── */
interface TrajectoryProps {
  trend: ScoreTrendPointVM[];
  passMark: number;
}

export function ScoreTrajectory({ trend, passMark }: TrajectoryProps) {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-[0_2px_4px_rgba(15,23,42,0.05),0_6px_16px_-6px_rgba(15,23,42,0.12)] dark:border-white/10 dark:bg-card-dark sm:p-6">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-[15px] font-extrabold text-slate-900 dark:text-white">
            {t('dashboard.variantB.trajectory.title')}
          </h3>
          <p className="mt-0.5 text-[12.5px] text-slate-500 dark:text-slate-400">
            {t('dashboard.variantB.trajectory.sub', { count: trend.length })}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-[11.5px] font-semibold text-slate-500 dark:text-slate-400">
          <Legend color="#2563EB" label={t('dashboard.variantB.trajectory.yourScore')} />
          <Legend color="#F59E0B" label={t('dashboard.variantB.trajectory.passLegend', { mark: passMark })} />
        </div>
      </div>
      <TrajectoryChart trend={trend} passMark={passMark} />
    </div>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-block h-2 w-2 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}

function TrajectoryChart({ trend, passMark }: TrajectoryProps) {
  const { t } = useTranslation();
  if (trend.length === 0) {
    return (
      <div className="flex h-48 items-center justify-center text-sm text-slate-500 dark:text-slate-400">
        {t('dashboard.variantB.trajectory.empty')}
      </div>
    );
  }
  const w = 640;
  const h = 220;
  const padL = 38;
  const padR = 18;
  const padT = 20;
  const padB = 34;
  const len = trend.length;
  const xs = trend.map((_, i) =>
    len === 1 ? padL + (w - padL - padR) / 2 : padL + (i * (w - padL - padR)) / (len - 1),
  );
  const ys = trend.map((p) => padT + (1 - p.score / 100) * (h - padT - padB));
  const line = xs.map((x, i) => `${i ? 'L' : 'M'}${x.toFixed(1)} ${ys[i].toFixed(1)}`).join(' ');
  const area = `${line} L${xs[xs.length - 1].toFixed(1)} ${h - padB} L${xs[0].toFixed(1)} ${h - padB} Z`;
  const targetY = padT + (1 - passMark / 100) * (h - padT - padB);
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="h-auto w-full" preserveAspectRatio="none" role="img" aria-label={t('dashboard.variantB.trajectory.ariaLabel')}>
      <defs>
        <linearGradient id="cm-trend-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#2563EB" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#2563EB" stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* Gridlines */}
      {[0, 25, 50, 75, 100].map((g) => {
        const y = padT + (1 - g / 100) * (h - padT - padB);
        return (
          <g key={g}>
            <line
              x1={padL}
              x2={w - padR}
              y1={y}
              y2={y}
              className="stroke-slate-200 dark:stroke-white/10"
              strokeWidth={1}
              strokeDasharray={g === 0 ? '0' : '3 5'}
              vectorEffect="non-scaling-stroke"
            />
            <text x={padL - 9} y={y + 3.5} textAnchor="end" className="fill-slate-400 dark:fill-slate-500 font-mono" fontSize={9.5}>
              {g}
            </text>
          </g>
        );
      })}
      {/* Pass line */}
      <line
        x1={padL}
        x2={w - padR}
        y1={targetY}
        y2={targetY}
        stroke="#F59E0B"
        strokeWidth={1.4}
        vectorEffect="non-scaling-stroke"
      />
      <text x={w - padR - 2} y={targetY - 6} textAnchor="end" fill="#D97706" fontSize={10} fontWeight={800} className="font-sans">
        PASS {passMark}
      </text>
      {/* Area + line */}
      <path d={area} fill="url(#cm-trend-grad)" />
      <path d={line} fill="none" stroke="#2563EB" strokeWidth={2.4} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      {/* Points */}
      {xs.map((x, i) => {
        const last = i === xs.length - 1;
        return (
          <g key={i}>
            <circle
              cx={x}
              cy={ys[i]}
              r={last ? 5 : 3.4}
              fill={last ? '#1D4ED8' : 'currentColor'}
              className={last ? '' : 'text-white dark:text-slate-900'}
              stroke="#2563EB"
              strokeWidth={2}
              vectorEffect="non-scaling-stroke"
            />
            {last && (
              <text x={x} y={ys[i] - 12} textAnchor="middle" fill="#1D4ED8" fontSize={12} fontWeight={800} className="font-mono">
                {trend[i].score}
              </text>
            )}
          </g>
        );
      })}
      {/* X-axis labels */}
      {xs.map((x, i) => (
        <text key={`x${i}`} x={x} y={h - padB + 18} textAnchor="middle" className="fill-slate-400 dark:fill-slate-500 font-mono" fontSize={9.5}>
          #{trend[i].n}
        </text>
      ))}
    </svg>
  );
}
